import './index.css'

import get from 'lodash/get'
import * as React from 'react'
import { connect } from 'react-redux'
import MediaQuery from 'react-responsive'
import { Outlet } from 'react-router-dom'

import {
  CB_59_GROWTH_AT_MANAGE_SUBSCRIPTION,
  PLAN_ACTIVE,
} from '@disco/constants/FeatureTypes'
import { sendMixpanelEvent } from '@disco/state-management/actions/AnalyticsActions'
import { toggleSidebar } from '@disco/state-management/actions/AppActions'
import { isDeviceTypePhone } from '@disco/state-management/selectors/app'
import { getFeatureData } from '@disco/state-management/selectors/business'
import {
  getConstanceSettings,
  getNumberOfUsers,
  getUser,
} from '@disco/state-management/selectors/user'

import type { User } from 'types/user'

import Pricing2021 from 'components/common/pricing2021'
import SubscribedUserBilling from 'components/common/subscribed-user-billing'
import TrialSignup from 'components/common/trial-signup'
import { showsTrialSignup } from 'components/common/trial-signup/utils'
import YourSubscription from 'components/common/your-subscription/your-subscription-container'
import DocumentHeadTags from 'components/document-head-tags'
import { Heading } from 'components/ds'
import Alert from 'components/ui/alert'
import Button from 'components/ui/button'
import PageContainer from 'components/ui/page-container'
import PageHeader from 'components/ui/page-header'
import Spinner from 'components/ui/spinner'

import UpdateSuccessPlanModal from './update-success-plan'

function mapStateToProps(state) {
  const { isFeatureEnabled: activePlan, isTrial } = getFeatureData(state, {
    featureName: PLAN_ACTIVE,
  })
  const user = getUser(state)
  return {
    deprecatedPlan: !activePlan,
    isTrial,

    /* TODO: refactoring:
      maxFilesCurrentPlan is the code from the old Pricing component.
      Previously, maxFilesCurrentPlan was used in the plan upgrade/downgrade logic.
      Currently Pricing component does not include the upgrade/downgrade plan flow
      asana task: https://app.asana.com/0/0/1201426247015104/f
    */
    maxFilesCurrentPlan: user.business.stats.total_track_count,

    /* TODO: refactoring:
        maxUsersCurrentPlan is the code from the old Pricing component.
        Previously, maxUsersCurrentPlan was used in the plan upgrade/downgrade logic.
        Currently Pricing component does not include the upgrade/downgrade plan flow
        asana task: https://app.asana.com/0/0/1201426247015104/f
      */
    maxUsersCurrentPlan: getNumberOfUsers(state),
    userData: user,
    sidebarOpen: state.app.sidebarOpen,
    planUpdateLoad: state.user.planUpdateLoad,
    isPhone: isDeviceTypePhone(state),
    showYourSubscription: Boolean(
      getConstanceSettings(state)[CB_59_GROWTH_AT_MANAGE_SUBSCRIPTION],
    ),
    showTrialSignup: showsTrialSignup(state),
  }
}

const actions = {
  sendMixpanelEvent,
  toggleSidebar,
}
type Props = typeof actions & {
  deprecatedPlan: boolean
  isTrial: boolean
  maxFilesCurrentPlan: number
  maxUsersCurrentPlan: number
  sendMixpanelEvent: (...args: Array<any>) => any
  userData: User
  sidebarOpen: boolean
  planUpdateLoad: boolean
  isPhone: boolean
  showYourSubscription: boolean
  showTrialSignup: boolean
}

class Billing extends React.Component<
  Props,
  {
    showPlanUpdateModal: boolean
  }
> {
  billingRef = React.createRef<HTMLDivElement>()
  state = {
    showPlanUpdateModal: false,
  }

  componentDidMount() {
    this.props.sendMixpanelEvent('Open Billing tab in settings')
  }

  static getDerivedStateFromProps(props, state) {
    if (!state.showPlanUpdateModal && props.planUpdateLoad) {
      return {
        planUpdateLoad: true,
      }
    }

    return null
  }

  onClosePlanUpdateModal = () => {
    this.setState({
      showPlanUpdateModal: false,
    })
  }

  render() {
    const {
      deprecatedPlan,
      isTrial,
      maxFilesCurrentPlan,
      maxUsersCurrentPlan,
      userData,
      sidebarOpen,
      planUpdateLoad,
      isPhone,
      showYourSubscription,
      showTrialSignup,
    } = this.props
    const toggleSidebar = isPhone ? undefined : this.props.toggleSidebar

    if (planUpdateLoad) {
      return <Spinner />
    }

    const pricing2021Props: React.ComponentProps<typeof Pricing2021> = {
      deprecatedPlan: deprecatedPlan,
      maxFilesCurrentPlan,
      maxUsersCurrentPlan,
      billingRef: this.billingRef,
      rootPath: '/settings/billing',
      showBillingSummary: true,
    }
    const disabledUpdatePaymentDetails = !userData.permissions?.includes(
      'can_change_business_settings',
    )
    let content
    let title: React.ReactNode = (
      <span className="Billing_Title">
        Billing and subscription{' '}
        {userData.planUpdateLoad ? (
          <span className="Billing_UpdatingPlan">Updating plan...</span>
        ) : null}
      </span>
    )

    // Section title is fixed to the legacy size, which doesn't match the Figma
    const billingHeading = (
      <Heading as="h1" size="5" weight="bold" mb="5" mt="0">
        Billing and Usage
      </Heading>
    )

    if (!isTrial) {
      content = (
        <>
          {showYourSubscription ? (
            <>
              {billingHeading}
              <YourSubscription />
            </>
          ) : (
            <SubscribedUserBilling />
          )}
          {this.state.showPlanUpdateModal && (
            <UpdateSuccessPlanModal
              onRequestClose={this.onClosePlanUpdateModal}
            />
          )}
        </>
      )
      title = null
    } else if (showTrialSignup) {
      content = (
        <>
          {billingHeading}
          <TrialSignup />
        </>
      )
      title = null
    } else {
      return (
        <>
          <DocumentHeadTags title="Settings | Billing | DISCO" />
          {!isPhone && (
            <MediaQuery maxWidth={1299}>
              <PageHeader.Breadcrumbs
                breadcrumbs={{
                  content: 'Settings',
                  link: '/settings/account',
                }}
              />
            </MediaQuery>
          )}
          <div className="Billing_Pricing2021Container">
            <div
              className="Billing_Pricing2021FlexWrapper"
              ref={this.billingRef}
            >
              <MediaQuery minWidth={1300} maxWidth={1600}>
                <Pricing2021
                  {...pricing2021Props}
                  toggleSidebar={toggleSidebar}
                  sidebarOpen={sidebarOpen}
                />
              </MediaQuery>
              <MediaQuery maxWidth={1299}>
                <Pricing2021
                  {...pricing2021Props}
                  toggleSidebar={toggleSidebar}
                  sidebarOpen={sidebarOpen}
                />
              </MediaQuery>
              <MediaQuery minWidth={1601}>
                <Pricing2021 {...pricing2021Props} />
              </MediaQuery>
            </div>
          </div>
          <Outlet />
        </>
      )
    }

    const hideBilling = isPhone && !isTrial
    let action = get(
      userData,
      'business.private_metadata.subscription_id',
      null,
    ) && (
      <Button
        icon="credit-card"
        caption="Update payment details"
        link="/settings/billing/update"
        disabled={disabledUpdatePaymentDetails}
        tooltip={
          disabledUpdatePaymentDetails
            ? 'Only admins on your DISCO can update card information.'
            : undefined
        }
      />
    )

    // Manage billing inside Your subscription replaces this button
    if (hideBilling || showYourSubscription || showTrialSignup) {
      action = undefined
      title = undefined
    }

    return (
      <PageContainer.Section title={title} action={action}>
        <DocumentHeadTags title="Settings | Billing | DISCO" />
        {hideBilling ? ( // Do not render children to hide all internal modals
          // like /settings/billing/update-seats-and-tracks
          <Alert status="danger" icon="warning">
            To access your billing details, open DISCO on a desktop browser
          </Alert>
        ) : (
          <>
            {content}
            <Outlet />
          </>
        )}
      </PageContainer.Section>
    )
  }
}

export default connect(mapStateToProps, actions)(Billing) as any
